import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 4.0 — AIPromoGenerator
 * Button + collapsible card. Clicking "Generate Promo" calls the backend
 * which caches the result in Mongo. Copy-to-clipboard for each section.
 */
const CopyBtn = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="text-[11px] px-2 py-1 rounded-md border border-purple-500/40 text-purple-200 hover:bg-purple-500/20 transition-all shrink-0"
    >
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
};

export const AIPromoGenerator = ({ submissionId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  const [error, setError] = useState(null);

  const generate = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/track/${submissionId}/ai-promo${refresh ? "?refresh=true" : ""}`);
      setPromo(r.data.promo);
      setOpen(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Promo service unavailable";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (promo) {
      setOpen((v) => !v);
    } else {
      generate(false);
    }
  };

  return (
    <div className="mt-4" data-testid="ai-promo-section">
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid="ai-promo-generate-btn"
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
          loading
            ? "bg-gray-700 text-gray-400 cursor-not-allowed"
            : "bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white"
        }`}
      >
        {loading ? (
          <>
            <span className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
            Generating…
          </>
        ) : (
          <>✨ {promo ? (open ? "Hide Promo" : "Show Promo") : "Generate Promo"}</>
        )}
      </button>

      {open && promo && (
        <div
          className="mt-4 rounded-2xl border border-fuchsia-500/30 bg-black/40 p-5 space-y-4"
          data-testid="ai-promo-card"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-sm flex items-center gap-2">
              <span>✨</span> AI Promo Kit
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30">Beta</span>
            </h3>
            <button
              type="button"
              onClick={() => generate(true)}
              className="text-xs text-fuchsia-300 hover:text-white transition-colors"
              data-testid="ai-promo-regenerate"
            >
              Regenerate
            </button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-pink-300">Instagram Caption</span>
              <CopyBtn text={promo.instagram_caption} testid="copy-ig-caption" />
            </div>
            <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap bg-gray-900/40 rounded-lg p-3">
              {promo.instagram_caption}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-cyan-300">TikTok Caption</span>
              <CopyBtn text={promo.tiktok_caption} testid="copy-tt-caption" />
            </div>
            <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap bg-gray-900/40 rounded-lg p-3">
              {promo.tiktok_caption}
            </p>
          </div>

          {promo.hashtags?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-purple-300">Hashtags</span>
                <CopyBtn text={promo.hashtags.join(" ")} testid="copy-hashtags" />
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-promo-hashtags">
                {promo.hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-200 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {error && !open && (
        <p className="mt-2 text-sm text-red-300" data-testid="ai-promo-error">{error}</p>
      )}
    </div>
  );
};

export default AIPromoGenerator;
